import { FunctionComponent, KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { List, ListItem } from 'omakase';
import { PatronLocationFinderSearchSuggestion } from 'components/patron-location/PatronLocationFinderSearch';
import { searchResultsList } from './patronLocationCssDefs';

interface PatronLocationFinderSearchResultsProps {
  onItemKeyDown?(event: KeyboardEvent<HTMLElement>): void;
  onSuggestion(suggestion: PatronLocationFinderSearchSuggestion): void;
  selectedIndex: number;
  setSelectedIndex?(index: number): void;
  suggestions: PatronLocationFinderSearchSuggestion[];
  textPrimaryFormat?(suggestion: PatronLocationFinderSearchSuggestion): ReactNode;
  textSecondaryFormat?(suggestion: PatronLocationFinderSearchSuggestion): ReactNode;
}

interface PatronLocationFinderSearchResultsItemProps {
  onClick(): void;
  onFocus?(): void;
  onKeyDown?(event: KeyboardEvent<HTMLElement>): void;
  selected: boolean;
  textPrimary?: string;
  textSecondary?: string;
}

const PatronLocationFinderSearchResultsItem: FunctionComponent<
  PatronLocationFinderSearchResultsItemProps
> = ({ onClick, onFocus, onKeyDown, selected, textPrimary, textSecondary }) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [localSelected, setLocalSelected] = useState(selected);
  useEffect(() => {
    if (selected) {
      ref?.current?.focus();
    }
  }, [selected]);

  return (
    <ListItem
      onBlur={() => setLocalSelected(false)}
      onClick={onClick}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      ref={ref}
      selected={localSelected}
      secondaryText={textSecondary}
      size="large"
      weight="book"
      variant="basic"
    >
      {textPrimary}
    </ListItem>
  );
};

export const PatronLocationFinderSearchResults: FunctionComponent<
  PatronLocationFinderSearchResultsProps
> = ({
  onItemKeyDown,
  onSuggestion,
  selectedIndex,
  setSelectedIndex = () => undefined,
  suggestions,
}) => {
  if (!suggestions.length) {
    return null;
  }

  return (
    <List css={searchResultsList} aria-label="Location search results">
      {suggestions.map((suggestion, i) => (
        <PatronLocationFinderSearchResultsItem
          key={suggestion.placeId}
          onClick={() => onSuggestion(suggestion)}
          onFocus={() => setSelectedIndex(i)}
          onKeyDown={onItemKeyDown}
          selected={i === selectedIndex}
          textPrimary={suggestion.primaryText}
          textSecondary={suggestion.secondaryText}
        />
      ))}
    </List>
  );
};
